import React, { useEffect, useState } from 'react';

import { LoginModal, Props as LoginModalProps } from './LoginModal';
import {
  PartialClickTrackingOptions,
  PartialConfigurableText,
  RegistrationParams,
} from './types';

type LoginModalEventDetail = {
  postLoginRedirectUrl?: string;
  forcePostLoginRedirectUrl?: boolean;
  clickTrackingContext?: PartialClickTrackingOptions;
  configuredText?: PartialConfigurableText;
  registrationParams: RegistrationParams;
};

// # Usage
//
// It isn't necessary to use openLoginModal directly. The LoginModalManager will listen for
// the 'open-login-modal' event and open the modal when it is triggered. Example:
//
// document.dispatchEvent(new CustomEvent('open-login-modal', {
//   detail: {
//     postLoginRedirectUrl: '/dashboard',
//     configuredText: {
//       buttonsPage: {
//         header: 'Custom welcome message'
//       }
//     },
//     registrationParams: {
//       user_source: 'header_button'
//     }
//   }
// }));
//
export const openLoginModal = (detail: LoginModalEventDetail) => {
  document.dispatchEvent(new CustomEvent('open-login-modal', { detail }));
};

export const LoginModalManager: React.FC = () => {
  const [modalProps, setModalProps] = useState<LoginModalProps>({
    open: false,
    registrationParams: {
      user_source: 'unknown', // user_source is required in event so this should never actually be sent
    },
  });

  useEffect(() => {
    const handleOpenLoginModal = (
      event: CustomEventInit<LoginModalEventDetail>,
    ) => {
      const { detail } = event;
      if (!detail) {
        throw new Error('No detail provided to open-login-modal event');
      }

      setModalProps(() => ({
        open: true,
        postLoginRedirectUrl: detail.postLoginRedirectUrl,
        forcePostLoginRedirectUrl: detail.forcePostLoginRedirectUrl,
        clickTrackingContext: detail.clickTrackingContext,
        configuredText: detail.configuredText,
        registrationParams: detail.registrationParams,
      }));
    };

    document.addEventListener('open-login-modal', handleOpenLoginModal);

    return () => {
      document.removeEventListener('open-login-modal', handleOpenLoginModal);
    };
  }, []);

  const handleClose = () => {
    setModalProps((prev) => ({
      ...prev,
      open: false,
    }));
  };

  return <LoginModal {...modalProps} onClose={handleClose} />;
};
